
import React, { useState } from 'react';
import { User, UserRole, Company } from '../../types';
import { Card, Button, Input, Badge } from '../UI';
import { useTitle, PAGE_TITLES } from '../../hooks';
import { Plus, User as UserIcon, Shield, Mail, Check, Building, Filter, Edit2, Trash2, Save, CornerDownRight, Folder, Search, X, Key, RefreshCw, Eye, EyeOff } from 'lucide-react';

interface UserManagerProps {
  users: User[];
  currentUser: User;
  companies: Company[]; // All companies available to the current user to assign
  onCreateUser: (user: User & { password: string }) => void;
  onUpdateUser: (user: User) => void;
  onRemoveUser: (userId: string) => void;
}

// Generate a random password
const generatePassword = (length: number = 12): string => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = '!@#$%^&*';
  const allChars = uppercase + lowercase + numbers + special;
  
  // Ensure at least one of each type
  let password = 
    uppercase[Math.floor(Math.random() * uppercase.length)] +
    lowercase[Math.floor(Math.random() * lowercase.length)] +
    numbers[Math.floor(Math.random() * numbers.length)] +
    special[Math.floor(Math.random() * special.length)];
  
  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

export const UserManager: React.FC<UserManagerProps> = ({ 
  users, 
  currentUser, 
  companies, 
  onCreateUser, 
  onUpdateUser, 
  onRemoveUser
}) => {
  // Page title
  useTitle(PAGE_TITLES.ADMIN_USERS);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    password: string;
    role: UserRole;
    allowedCompanyIds: string[];
  }>({
    name: '',
    email: '',
    password: '',
    role: UserRole.STAF_FINANCE,
    allowedCompanyIds: []
  });

  const [showPassword, setShowPassword] = useState(false);

  // Role Logic:
  // Administrator can create everyone.
  // Staf Admin can create everyone except Administrator.
  // Staf HO can create Staf Finance.
  const canCreateUser = currentUser.role === UserRole.ADMINISTRATOR || currentUser.role === UserRole.STAF_ADMIN || currentUser.role === UserRole.STAF_HO;
  
  const getAvailableRoles = () => {
      switch(currentUser.role) {
          case UserRole.ADMINISTRATOR:
              return [UserRole.STAF_ADMIN, UserRole.STAF_HO, UserRole.STAF_FINANCE];
          case UserRole.STAF_ADMIN:
               return [UserRole.STAF_HO, UserRole.STAF_FINANCE];
          case UserRole.STAF_HO:
              return [UserRole.STAF_FINANCE];
          default:
              return [];
      }
  };

  const availableRoles = React.useMemo(() => getAvailableRoles(), [currentUser.role]);

  // Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<UserRole | 'all'>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Filtered users
  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Role filter
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  const activeFiltersCount = (searchQuery ? 1 : 0) + (filterRole !== 'all' ? 1 : 0);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterRole('all');
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', role: UserRole.STAF_FINANCE, allowedCompanyIds: [] });
    setEditingUserId(null);
    setShowPassword(false);
  };

  const handleOpenCreateUser = () => {
    resetForm();
    // Auto-generate a password for new users
    setFormData(prev => ({ ...prev, password: generatePassword() }));
    setIsModalOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    setEditingUserId(user.id);
    setFormData({
        name: user.name,
        email: user.email,
        password: '', // Password not shown when editing
        role: user.role,
        allowedCompanyIds: user.allowedCompanyIds || []
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingUserId) {
        onUpdateUser({
            id: editingUserId,
            name: formData.name,
            email: formData.email,
            role: formData.role,
            allowedCompanyIds: formData.allowedCompanyIds
        });
    } else {
        // Generate name from email (extract part before @, replace dots/underscores with spaces, title case)
        const emailPrefix = formData.email.split('@')[0];
        const generatedName = emailPrefix
          .replace(/[._]/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());
        
        onCreateUser({
            id: `u-${Date.now()}`,
            name: generatedName,
            email: formData.email,
            role: formData.role,
            allowedCompanyIds: formData.allowedCompanyIds,
            password: formData.password
        });
    }
    
    setIsModalOpen(false);
    resetForm();
  };

  const toggleCompanySelection = (companyId: string) => {
    setFormData(prev => {
      const exists = prev.allowedCompanyIds.includes(companyId);
      return {
        ...prev,
        allowedCompanyIds: exists 
          ? prev.allowedCompanyIds.filter(id => id !== companyId)
          : [...prev.allowedCompanyIds, companyId]
      };
    });
  };

  const selectAllCompanies = () => {
     if (formData.allowedCompanyIds.length === companies.length) {
         setFormData(prev => ({ ...prev, allowedCompanyIds: [] }));
     } else {
         setFormData(prev => ({ ...prev, allowedCompanyIds: companies.map(c => c.id) }));
     }
  };

  // Permission Check Helper
  const canModifyUser = (targetUser: User) => {
      if (currentUser.id === targetUser.id) return false; // Can't edit self here (usually done in profile)
      if (currentUser.role === UserRole.ADMINISTRATOR) return true;
      if (currentUser.role === UserRole.STAF_ADMIN && targetUser.role !== UserRole.ADMINISTRATOR) return true;
      if (currentUser.role === UserRole.STAF_HO && targetUser.role === UserRole.STAF_FINANCE) return true;
      return false;
  };

  const getRoleBadgeColor = (role: UserRole) => {
      switch(role) {
          case UserRole.ADMINISTRATOR: return 'indigo';
          case UserRole.STAF_ADMIN: return 'purple';
          case UserRole.STAF_HO: return 'blue';
          case UserRole.STAF_FINANCE: return 'green';
          default: return 'gray';
      }
  };

  // Helper to build tree structure for the modal checkboxes
  const getCompanyTree = () => {
    const roots = companies.filter(c => !c.parentId);
    const tree: { node: Company, children: Company[] }[] = [];
    
    roots.forEach(root => {
        const children = companies.filter(c => c.parentId === root.id);
        tree.push({ node: root, children });
    });
    
    const processedIds = new Set([
        ...roots.map(r => r.id), 
        ...roots.flatMap(r => companies.filter(c => c.parentId === r.id).map(c => c.id))
    ]);
    
    companies.forEach(c => {
        if (!processedIds.has(c.id)) {
            tree.push({ node: c, children: [] });
        }
    });

    return tree;
  };

  const companyTree = React.useMemo(() => getCompanyTree(), [companies]);

  const renderCompanyCheckbox = (company: Company, level: number = 0, isLastChild: boolean = false) => {
      const isSelected = formData.allowedCompanyIds.includes(company.id);
      
      return (
        <div 
            key={company.id} 
            onClick={() => toggleCompanySelection(company.id)}
            className={`
                flex items-center px-4 py-2 cursor-pointer transition-colors border-b border-gray-50 last:border-0
                ${isSelected ? 'bg-indigo-50/40' : 'hover:bg-gray-50'}
            `}
            style={{ paddingLeft: `${level === 0 ? 1 : level * 2.5}rem` }}
        >
            {level > 0 && (
                 <CornerDownRight className="w-4 h-4 text-gray-300 mr-2 shrink-0" />
            )}

            <div className={`
                w-5 h-5 rounded border flex items-center justify-center mr-3 transition-all shrink-0
                ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white group-hover:border-indigo-400'}
            `}>
                {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
            </div>

            <div className="flex-1 min-w-0 flex items-center">
                {company.isHolding ? (
                    <Folder className="w-4 h-4 text-indigo-400 mr-2" />
                ) : (
                    <Building className="w-4 h-4 text-gray-400 mr-2" />
                )}
                <div className="flex flex-col">
                    <span className={`text-sm ${company.isHolding ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>
                        {company.name}
                    </span>
                    {company.isHolding && (
                        <span className="text-[10px] text-gray-400 leading-none">Holding Company</span>
                    )}
                </div>
            </div>
        </div>
      );
  };

  return (
    <div className="space-y-6">
      {/* Header with Search and Filter */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
             <h2 className="text-lg font-bold text-gray-900">User Management</h2>
             <p className="text-sm text-gray-500 mt-1">Manage team access and company permissions.</p>
          </div>
          <div className="flex space-x-3">
               <Button 
                 variant="secondary"
                 onClick={() => setIsFilterOpen(!isFilterOpen)}
                 className={activeFiltersCount > 0 ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}
               >
                  <Filter className="w-4 h-4 mr-2" /> 
                  Filter
                  {activeFiltersCount > 0 && (
                    <span className="ml-2 bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
              </Button>
              {canCreateUser && (
                <Button onClick={handleOpenCreateUser}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
              )}
          </div>
        </div>

        {/* Filter Panel */}
        {isFilterOpen && (
          <div className="pt-4 border-t border-gray-100 animate-fade-in-up">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari nama atau email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Role Filter */}
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value as UserRole | 'all')}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="all">Semua Role</option>
                <option value={UserRole.ADMINISTRATOR}>Administrator</option>
                <option value={UserRole.STAF_ADMIN}>Staf Admin</option>
                <option value={UserRole.STAF_HO}>Staf HO</option>
                <option value={UserRole.STAF_FINANCE}>Staf Finance</option>
              </select>

              {/* Clear Filters */}
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-indigo-600 transition-colors whitespace-nowrap"
                >
                  Hapus Filter
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      {(searchQuery || filterRole !== 'all') && (
        <p className="text-sm text-gray-500">
          Menampilkan {filteredUsers.length} dari {users.length} pengguna
        </p>
      )}

      <Card className="overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Tidak ada hasil</h3>
            <p className="text-sm text-gray-500 mb-4">Coba ubah kata kunci atau filter pencarian</p>
            <Button variant="secondary" onClick={clearFilters}>
              Hapus Filter
            </Button>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="sm:hidden divide-y divide-gray-100">
              {filteredUsers.map((user) => (
                <div key={user.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                        {user.name.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{user.name}</div>
                        <div className="text-xs text-gray-500 flex items-center mt-0.5 truncate">
                          <Mail className="w-3 h-3 mr-1 shrink-0" /> 
                          <span className="truncate">{user.email}</span>
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <Badge color={getRoleBadgeColor(user.role)}>
                        {user.role.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {user.role === UserRole.ADMINISTRATOR || user.role === UserRole.STAF_ADMIN ? (
                        <span className="text-xs text-gray-400 italic">All Companies</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {user.allowedCompanyIds && user.allowedCompanyIds.length > 0 ? (
                            <>
                              {user.allowedCompanyIds.slice(0, 2).map(cid => {
                                const c = companies.find(comp => comp.id === cid);
                                return c ? (
                                  <span key={cid} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 truncate max-w-[100px]">
                                    {c.name}
                                  </span>
                                ) : null;
                              })}
                              {user.allowedCompanyIds.length > 2 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                                  +{user.allowedCompanyIds.length - 2}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-red-400">No access</span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 shrink-0">
                      {canModifyUser(user) && (
                        <>
                          <button 
                            onClick={() => handleOpenEdit(user)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors touch-target"
                            title="Edit Access"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => {
                              if(confirm(`Are you sure you want to remove ${user.name}?`)) {
                                onRemoveUser(user.id);
                              }
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors touch-target"
                            title="Remove User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {user.id === currentUser.id && (
                        <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded">You</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned Companies</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                            {user.name.charAt(0)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                            <div className="text-xs text-gray-500 flex items-center mt-0.5">
                              <Mail className="w-3 h-3 mr-1" /> {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge color={getRoleBadgeColor(user.role)}>
                          {user.role.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {user.role === UserRole.ADMINISTRATOR || user.role === UserRole.STAF_ADMIN ? (
                          <span className="text-xs text-gray-400 italic">All Companies (Full Access)</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {user.allowedCompanyIds && user.allowedCompanyIds.length > 0 ? (
                              <>
                                {user.allowedCompanyIds.slice(0, 2).map(cid => {
                                  const c = companies.find(comp => comp.id === cid);
                                  return c ? (
                                    <span key={cid} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                                      {c.name}
                                    </span>
                                  ) : null;
                                })}
                                {user.allowedCompanyIds.length > 2 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                                    +{user.allowedCompanyIds.length - 2} more
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-red-400">No access assigned</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {canModifyUser(user) && (
                          <div className="flex items-center justify-end space-x-2">
                            <button 
                              onClick={() => handleOpenEdit(user)}
                              className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 transition-colors"
                              title="Edit Access"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => {
                                if(confirm(`Are you sure you want to remove ${user.name}? This action cannot be undone.`)) {
                                  onRemoveUser(user.id);
                                }
                              }}
                              className="text-gray-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                              title="Remove User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        {user.id === currentUser.id && (
                          <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded">You</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* Invite/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-100 flex items-end sm:items-center justify-center sm:p-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" 
            onClick={() => setIsModalOpen(false)} 
          />
          
          {/* Modal Content */}
          <div className="relative bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[90vh] flex flex-col animate-slideUp sm:animate-scaleIn border border-gray-100">
            {/* Mobile drag indicator */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
            
            <div className="px-4 pt-6 pb-4 sm:px-8 sm:pt-8 shrink-0">
              <div className="flex justify-between items-start mb-6 pt-2 sm:pt-0">
                  <div>
                      <h3 className="text-lg sm:text-xl leading-6 font-bold text-gray-900">
                          {editingUserId ? 'Update User Access' : 'Create New User'}
                      </h3>
                      <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-500">
                          {editingUserId ? 'Modify roles and assigned companies.' : 'Create a new user account.'}
                      </p>
                  </div>
                  <button 
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg touch-target"
                  >
                    <X className="w-5 h-5" />
                  </button>
              </div>
            </div>
              
            <div className="px-4 sm:px-8 overflow-y-auto flex-1">
              <form id="user-form" onSubmit={handleSubmit} className="space-y-6 pb-4">
                {!editingUserId ? (
                  /* Create User Form */
                  <div className="space-y-4">
                    <Input 
                      label="Email Address" 
                      type="email"
                      startIcon={Mail}
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="e.g. john.doe@wbbgroup.com"
                      required
                    />
                    
                    {/* Password Field with Generate Button */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                        Password
                      </label>
                      <div className="relative flex gap-2">
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Key className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono"
                            placeholder="Password"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, password: generatePassword()})}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium"
                          title="Generate new password"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Generate
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 ml-1">
                        Auto-generated name: <span className="font-medium text-gray-700">{formData.email ? formData.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '...'}</span>
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Edit User Form */
                  <div className="grid grid-cols-2 gap-4">
                    <Input 
                      label="Full Name" 
                      startIcon={UserIcon}
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                    <Input 
                      label="Email Address" 
                      type="email" 
                      startIcon={Mail} 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">Role Assignment</label>
                  <div className="grid grid-cols-2 gap-3">
                      {availableRoles.map(role => (
                          <div 
                              key={role}
                              onClick={() => setFormData({...formData, role})}
                              className={`
                                  cursor-pointer rounded-lg border p-3 flex items-center space-x-3 transition-all
                                  ${formData.role === role ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-gray-200 hover:border-gray-300'}
                              `}
                          >
                              <div className={`p-1.5 rounded-full ${formData.role === role ? 'bg-indigo-200' : 'bg-gray-100'}`}>
                                  <Shield className={`w-4 h-4 ${formData.role === role ? 'text-indigo-700' : 'text-gray-500'}`} />
                              </div>
                              <div>
                                  <div className={`text-sm font-medium ${formData.role === role ? 'text-indigo-900' : 'text-gray-900'}`}>
                                      {role.replace(/_/g, ' ')}
                                  </div>
                              </div>
                              {formData.role === role && <Check className="w-4 h-4 text-indigo-600 ml-auto" />}
                          </div>
                      ))}
                  </div>
                </div>

                {formData.role !== UserRole.ADMINISTRATOR && formData.role !== UserRole.STAF_ADMIN && (
                  <div className="space-y-2">
                      <div className="flex items-center justify-between">
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">Access Scope</label>
                          <button 
                              type="button" 
                              onClick={selectAllCompanies}
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                              {formData.allowedCompanyIds.length === companies.length ? 'Deselect All' : 'Select All'}
                          </button>
                      </div>
                      <div className="border border-gray-200 rounded-xl overflow-hidden max-h-56 overflow-y-auto bg-white custom-scrollbar shadow-inner">
                          {companies.length === 0 ? (
                              <div className="p-4 text-center text-gray-500 text-sm">No companies available to assign.</div>
                          ) : (
                              <div className="divide-y divide-gray-50">
                                  {companyTree.map(({node, children}) => (
                                      <React.Fragment key={node.id}>
                                          {renderCompanyCheckbox(node, 0)}
                                          {children.map(child => renderCompanyCheckbox(child, 1))}
                                      </React.Fragment>
                                  ))}
                              </div>
                          )}
                      </div>
                  </div>
                )}

              </form>
            </div>
            <div className="bg-gray-50 px-4 py-4 sm:px-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-100 shrink-0 pb-safe">
              <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)} className="w-full sm:w-auto" fullWidthMobile>
                Cancel
              </Button>
              <Button type="submit" form="user-form" className="w-full sm:w-auto" fullWidthMobile>
                {editingUserId ? <><Save className="w-4 h-4 mr-2"/>Save Changes</> : 'Create User'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
